/**
 * Provenance Badge Component
 * 
 * Shows the source of an asset with visual indicators:
 * - ● Connected (synced from provider)
 * - ■ Manual (user-entered)
 * - ▲ Account (account balance)
 */
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type SourceType = 'account' | 'connected' | 'manual';

interface ProvenanceBadgeProps {
  source: SourceType;
  className?: string;
}

export function ProvenanceBadge({ 
  source, 
  className 
}: ProvenanceBadgeProps) {
  const tooltips: Record<SourceType, string> = {
    account: 'Account',
    connected: 'Holding',
    manual: 'Manual',
  };

  const tooltip = tooltips[source];

  // Consistent 16x16 viewBox and 12-unit shapes for equal visual weight
  const icon = (
    <svg
      viewBox="0 0 16 16"
      width="12"
      height="12"
      aria-hidden="true"
      focusable="false"
      className="block"
    >
      {source === 'connected' && (
        <circle cx="8" cy="8" r="6" fill="currentColor" />
      )}
      {source === 'manual' && (
        <rect x="2" y="2" width="12" height="12" fill="currentColor" />
      )}
      {source === 'account' && (
        // Slightly larger triangle to match visual weight of circle/square
        <polygon points="8,1 15,15 1,15" fill="currentColor" />
      )}
    </svg>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center justify-center w-4 h-4 text-muted-foreground opacity-60 cursor-default ${className || ''}`}>
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
